const toneClassMap = {
  error: "fittr-ios-toast--error ftr-toast--error",
  warning: "fittr-ios-toast--warning ftr-toast--warning",
  success: "fittr-ios-toast--success ftr-toast--success",
  info: "fittr-ios-toast--info ftr-toast--info",
};

const iconMap = {
  error: "fa-solid fa-triangle-exclamation",
  warning: "fa-solid fa-circle-exclamation",
  success: "fa-solid fa-circle-check",
  info: "fa-solid fa-circle-info",
};

const toneByType = {
  negative: "error",
  error: "error",
  positive: "success",
  success: "success",
  warning: "warning",
  info: "info",
};

const inferTone = (options = {}) => {
  const className = String(options.classes || "");
  if (className.includes("fittr-ios-toast--error") || className.includes("ftr-toast--error")) {
    return "error";
  }
  if (className.includes("fittr-ios-toast--success") || className.includes("ftr-toast--success")) {
    return "success";
  }
  if (className.includes("fittr-ios-toast--info") || className.includes("ftr-toast--info")) {
    return "info";
  }
  if (className.includes("fittr-ios-toast--warning") || className.includes("ftr-toast--warning")) {
    return "warning";
  }

  const explicitTone = toneByType[options.tone] || toneByType[options.type];
  if (explicitTone) {
    return explicitTone;
  }

  const color = String(options.color || "").toLowerCase();
  if (/(negative|danger|red|error)/.test(color)) {
    return "error";
  }
  if (/(positive|success|green)/.test(color)) {
    return "success";
  }
  if (/(warning|orange|amber|yellow)/.test(color)) {
    return "warning";
  }
  if (/(info|blue|cyan|teal)/.test(color)) {
    return "info";
  }

  return "warning";
};

const stripToneClasses = (classes = "") => (
  String(classes)
    .split(/\s+/)
    .filter((className) => (
      className
      && className !== "fittr-ios-toast"
      && className !== "ftr-toast"
      && !className.startsWith("fittr-ios-toast--")
      && !className.startsWith("ftr-toast--")
    ))
    .join(" ")
);

export const normalizeToastOptions = (options = {}) => {
  const sourceOptions = typeof options === "string" ? { message: options } : (options || {});
  const resolvedTone = toneClassMap[sourceOptions.tone]
    ? sourceOptions.tone
    : inferTone(sourceOptions);
  const customClasses = stripToneClasses(sourceOptions.classes);

  return {
    ...sourceOptions,
    position: sourceOptions.position || "center",
    timeout: sourceOptions.timeout ?? 2800,
    icon: sourceOptions.icon || iconMap[resolvedTone],
    classes: [
      "fittr-ios-toast",
      "ftr-toast",
      toneClassMap[resolvedTone],
      customClasses,
    ].filter(Boolean).join(" "),
    color: undefined,
    textColor: undefined,
    tone: undefined,
  };
};

export const getToastOptions = ({
  message,
  tone = "info",
  icon,
  timeout = 2800,
  position = "center",
  caption,
  actions,
  ...rest
} = {}) => {
  const resolvedTone = toneClassMap[tone] ? tone : "info";

  return normalizeToastOptions({
    position,
    timeout,
    icon: icon || iconMap[resolvedTone],
    message,
    caption,
    actions,
    classes: [
      "fittr-ios-toast",
      "ftr-toast",
      toneClassMap[resolvedTone],
      rest.classes,
    ].filter(Boolean).join(" "),
    ...rest,
  });
};

export const notifyToast = ($q, options = {}) => {
  if (!$q?.notify) {
    return undefined;
  }

  return $q.notify(getToastOptions(options));
};

export const installGlobalToastNormalization = ($q, notifyPlugin) => {
  if (!$q?.notify || $q.notify.__fittrToastNormalized) {
    return;
  }

  const originalNotify = $q.notify.bind($q);
  const normalizedNotify = (options) => originalNotify(normalizeToastOptions(options));
  normalizedNotify.setDefaults = $q.notify.setDefaults;
  normalizedNotify.registerType = $q.notify.registerType;
  normalizedNotify.__fittrToastNormalized = true;
  $q.notify = normalizedNotify;

  if (notifyPlugin?.create && !notifyPlugin.create.__fittrToastNormalized) {
    const originalCreate = notifyPlugin.create.bind(notifyPlugin);
    notifyPlugin.create = (options) => originalCreate(normalizeToastOptions(options));
    notifyPlugin.create.__fittrToastNormalized = true;
  }
};
